import React, { useCallback, useMemo, useRef, useState } from 'react';
import swal from 'sweetalert';

import { doc, DocumentSnapshot, updateDoc } from 'firebase/firestore';
import useFetchUsers from '../hooks/users';
import Pagination from '../components/Pagination';
import { LogoImageUserDefault } from '../components/Images';
import { ArrowDownCircleIcon, ArrowDownTrayIcon, ArrowUpCircleIcon, ArrowUpTrayIcon, EyeIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';
import Badges from '../components/Badges';
import useDeleteUser from '../hooks/deleteUser';
import { firebaseDB, firebaseAuth } from '../firebase/config';
import { toast } from 'react-toastify';
import { getAuth } from 'firebase/auth';

const UsersPage: React.FC = () => {
	const navigate = useNavigate();
	const { deleteUser } = useDeleteUser();
	
	const [page, setPage] = useState(0);
	const [itemsPerPage, setItemsPerPage] = useState<number>(10);
	const [searchTerm, setSearchTerm] = useState<string>('');
 
  const cursors = useRef<Map<number, DocumentSnapshot>>(new Map());
 
  const results = useFetchUsers(searchTerm, {
    cursor: cursors.current.get(page),
    itemsPerPage,
  });

  const users = useMemo(() => {
    const combinedResults = results.flatMap((result) => result.data?.docs.map((doc) => doc.data()) ?? []);
    const uniqueUsers = Array.from(new Map(combinedResults.map((user: any) => [user.id, user])).values());

    return uniqueUsers;
  }, [results]);

	const onPageChanged = useCallback((nextPage: number) => {
			setPage((page) => {
				cursors.current.set(
					page + 1,
					results[0].data.docs[results[0].data.docs.length - 1]
				);

				return nextPage;
			});
		},
		[results, users, page]
  );
	
  if (results.some((result) => result.status === 'loading')) {
    return <span>Loading Users...</span>;
  }

	const searchActivitiesFavorite = (activities: string[]) => {
		const experiencesTypes = [
			{ id: "1", label: "Food & Drinks" },
			{ id: "2", label: "Museums & Galleries"},
			{ id: "3", label: "Outdoors"},
			{ id: "4", label: "Shows & Concerts"},
			{ id: "5", label: "Sight-see & Tours"},
			{ id: "6", label: "Sport Events"},
			{ id: "7", label: "Volunteering"},
			{ id: "8", label: "Fitness & Wellness"},
		];

		const activitiesFavorite = experiencesTypes.filter((type) => activities.includes(type.id));

		return activitiesFavorite.map((activity) => activity.label).join(', ');
	}

	const handlerDelete = async (userId: string) => {
		const swalAction = await swal("You want to delete the user?", {
			dangerMode: true,
			buttons: ['Cancel', 'Delete'],
		});

		if(swalAction)  {
			await deleteUser(userId);
		}
	}

	const handlerBlockOrUnBlock = async (userId: string, status: boolean) => {
		const auth = getAuth();
		const currentUser = auth.currentUser;
		
		if (!currentUser) {
			toast.error("No authenticated user found.");
			return;
		}
		
		const idToken = await currentUser.getIdToken();

		const swalAction = await swal(`Are you sure to ${status ? 'block' : 'unblock'} the user?`, {
			dangerMode: true,
			buttons: ['Cancel', 'Yes'],
		});

		if(swalAction)  {
			try {
				const userRef = doc(firebaseDB, 'users', userId);
				await updateDoc(userRef, { 
					status: status ? 'blocked' : 'active'
				});
				
				if(status) {
					const response = await fetch('https://us-central1-easymeet-26dd0.cloudfunctions.net/blockUser', {
						method: 'POST',
						headers: {
								'Content-Type': 'application/json',
								'Authorization': `Bearer ${idToken}`,
						},
						body: JSON.stringify({ uid: userId }),
					});
				}

				if(!status) {
					const response = await fetch('https://us-central1-easymeet-26dd0.cloudfunctions.net/unblockUser', {
						method: 'POST',
						headers: {
								'Content-Type': 'application/json',
								'Authorization': `Bearer ${idToken}`,
						},
						body: JSON.stringify({ uid: userId }),
					});
				}
				
				toast.success(`User ${status ? 'blocked' : 'unlocked'} successfully`);
			} catch(error: any) {
				toast.error(`Error: ${error?.message}`);
			}
		}
	}
	
	return (
		<div className="min-h-screen p-10 mb-20">
			<div className="w-100 block pb-6 border-b-2 border-primary-700">
				<h1 className="font-bold text-2xl">Users</h1>
			</div>

			<div className={'flex flex-col space-y-2 mt-6'}>
				<input
          type="text"
          placeholder="Search User damian@semnexus.com..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="mb-4 p-2 border border-gray-300 rounded"
        />

				<table className={'table-auto table-custom'}>
					<thead>
						<tr>
							<th>ID</th>
							<th>User</th>
							<th>Display name</th>
							<th>Badge</th>
							<th>Status</th>
							<th style={{ maxWidth: '100px' }}>Fav Activities</th>
							<th>Actions</th>
						</tr>
					</thead>
	
					<tbody>
						{ users.map((user, key) => {
								return (
									<tr key={key}>
										<td>
											<p>{ user.id.substring(0, 10) }</p>
										</td>
										<td>
											<li className="flex justify-between gap-x-6 pr-1 pl-3">
												<div className="flex w-100">
													<img className="h-12 w-12 flex-none rounded-full bg-gray-50" src={user.photoURL || LogoImageUserDefault} />
													<div className="min-w-0 flex-auto text-left ml-3">
														<p className="text-sm font-semibold leading-6 text-gray-900">{ user.name }</p>
														<p className="truncate text-xs leading-5 text-gray-500"><p>{ user.email }</p></p>
													</div>
												</div>
											</li>
										</td>
										<td>
											<p>@{ user.username }</p>
										</td>
										<td className='text-center'>
											<div className='text-center flex justify-center'>
												{ user.badge && <Badges badge={user.badge || ''} styles={{ width: '40px', height: '40px' }}/> }
												{ (!user.badge || user.badge == 'none') && <>N/A</> }
											</div>
										</td>
										<td>
											<p>{ user?.status || 'active' }</p>
										</td>
										<td style={{ maxWidth: '100px' }} className='truncate-text'>
											{ searchActivitiesFavorite(user?.activities || []) }
										</td>
										<td>
											<div className='flex justify-center'>
												<button 
													type="button"
													className="btn btn-sm btn-icon btn-danger mr-2"
													onClick={() => handlerDelete(user.id)}
												>
													<XMarkIcon className="size-4"/>
												</button>
												<button 
													type="button"
													className="btn btn-sm btn-icon btn-success mr-2"
													onClick={() => navigate('/users/' + user.id)}
												>
													<EyeIcon className="size-4"/>
												</button>
												
												{ user?.status != 'blocked' && <button 
														type="button"
														title='Block user'
														className="btn btn-sm btn-icon btn-warning active"
														onClick={() => handlerBlockOrUnBlock(user.id, true)}
													>
														<ArrowDownCircleIcon className="size-4"/>
													</button>
												}
												
												{ user?.status == 'blocked' && <button 
														type="button"
														title='Unblock user'
														className="btn btn-sm btn-icon btn-info active" 
														onClick={() => handlerBlockOrUnBlock(user.id, false)}
													>
														<ArrowUpCircleIcon className="size-4 text-white"/>
													</button>
												}

											</div>
										</td>
									</tr>
								);
							})
						}
					</tbody>
				</table>
	
				<Pagination
					currentPage={page}
					itemsPerPage={itemsPerPage}
					pageChanged={onPageChanged}
				/>
			</div>
		</div>
	);
};

export default UsersPage;